import { Component, ElementRef, Input, TemplateRef } from '@angular/core';
import { LogService } from 'src/app/my-sedgwick';
import { TranslationRequest } from '../../models/translation/translation-request';
import { BaseComponent } from '../base.component';

@Component({
    selector: 'app-panel',
    template: `
    @if (border === 'Empty') {
      <div
        class="tw-rounded-lg content  tw-w-full tw-h-full tw-overflow-hidden"
      [ngClass]="{
        'tw-drop-shadow-none': !elevated,
        'tw-drop-shadow-xl': elevated,
        'empty-border': shadow
      }"
        [style.min-height]="minHeight"
        >
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </div>
    }
    @if (border !== 'Empty') {
      <div
        class="tw-rounded-lg content tw-w-full tw-h-full tw-overflow-hidden tw-bg-white"
      [ngClass]="{
        'flat-panel': border === 'Flat',
        'flat-padded-panel': border === 'FlatPadded',
        'single-panel': border === 'Single',
        'tw-drop-shadow-none': !elevated,
        'tw-drop-shadow-xl': elevated
      }"
        [style.min-height]="minHeight"
        >
        <!-- removed header as a clickable element as it was affecting keyboard access for links coming through in templaterefs
        my claims link , manager view link, help icon click -->
        @if (showTitle) {
          <div
            class="tw-w-full tw-flex tw-flex-wrap tw-items-center tw-pt-0.5 tw-pb-1"
            >
            @switch (headerLevel) {
              @case (1) {
                <h1 class="tw-grow-0 tw-pr-2 tw-py-2">
                  {{ (sentenceCase ? (title | sentenceCase) : title) | translation: translationRequest }} {{ noTranslationText ? ' ' + noTranslationText : '' }}
                </h1>
              }
              @case (2) {
                <h2 class="tw-grow-0 tw-pr-2 tw-py-2">
                  {{ (sentenceCase ? (title | sentenceCase) : title) | translation: translationRequest }} {{ noTranslationText ? ' ' + noTranslationText : '' }}
                </h2>
              }
              @case (3) {
                <h3 class="tw-grow-0 tw-pr-2 tw-py-2">
                  {{ (sentenceCase ? (title | sentenceCase) : title) | translation: translationRequest }} {{ noTranslationText ? ' ' + noTranslationText : '' }}
                </h3>
              }
              @case (4) {
                <h4 class="tw-grow-0 tw-pr-2 tw-py-2">
                  {{ (sentenceCase ? (title | sentenceCase) : title) | translation: translationRequest }} {{ noTranslationText ? ' ' + noTranslationText : '' }}
                </h4>
              }
              @case (5) {
                <h5 class="tw-grow-0 tw-pr-2 tw-py-2">
                  {{ (sentenceCase ? (title | sentenceCase) : title) | translation: translationRequest }} {{ noTranslationText ? ' ' + noTranslationText : '' }}
                </h5>
              }
            }
            <div class="tw-grow tw-flex tw-items-left">
              <ng-container *ngTemplateOutlet="labelTemplate"></ng-container>
            </div>
            @if( canExpand ){
              <button
                [ngClass]="{ 'app-pointer': canExpand }"
                [attr.aria-expanded]="expanded"
          [attr.aria-label]="
            (expanded
              ? 'Collapse ' + title + ' panel'
              : 'Expand ' + title + ' panel') | translation: translationRequest
          "
                [attr.aria-disabled]="!canExpand"
                [attr.aria-controls]="panelId"
                (click)="toggleExpand(canExpand)"
                >
                <mat-icon class="expand-icon">{{
                  expanded ? 'expand_less' : 'expand_more'
                }}</mat-icon>
              </button>
            }
          </div>
        }
        <div
          [id]="panelId"
          role="region"
          class="tw-w-full"
          [hidden]="!expanded"
          [class.tw-pt-3]="showTitle"
          [class.tw-pb-4]="showTitle"
          >
          <ng-container *ngTemplateOutlet="content"></ng-container>
        </div>
      </div>
    }
    <ng-template #content>
      <ng-content></ng-content>
    </ng-template>
    `,
    styles: [
        `
      .expand-icon {
        color: var(--color-slate);
        cursor: pointer;
      }

      .flat-panel {
        padding: 4px 0px 0px 0px;
        background-color: var(--sed-warm-white-color);
      }

      .flat-padded-panel {
        padding: 0px 16px 0px 16px;
        background-color: var(--sed-warm-white-color);
      }

      .single-panel {
        padding: 0px 22px 0px 22px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 12px !important;
        transition: border 0.3s, box-shadow 0.3s;
      }

      .empty-border {
        transition: border 0.3s, box-shadow 0.3s;
      }

      .single-panel:hover,
      .empty-border:hover {
        border: 1px solid var(--sed-grey-100-color);
        box-shadow: 10px 10px 10px -2px rgba(0, 0, 0, 0.36);
      }

      :host {
        display: block;
        padding: 8px;
      }

      @media (max-width: 480px) {
        .single-panel {
          padding: 0px 8px 8px 8px;
          background-color: var(--sed-warm-white-color);
          border: 1px solid rgba(0, 0, 0, 0.12);
          border-radius: 12px !important;
        }

        .flat-padded-panel {
          padding: 8px;
          background-color: var(--sed-warm-white-color);
        }

        :host {
          display: block;
          padding: 4px;
        }
      }

      .content {
        font-size: 14px;
      }
    `,
    ],
    standalone: false
})
export class PanelComponent extends BaseComponent {
  @Input()
  showTitle = true;

  @Input()
  shadow = false;

  @Input()
  headerLevel: HeaderLevel = 1;

  @Input()
  noTranslationText: string = '';

  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });

  @Input()
  get title(): string {
    return this._title;
  }
  set title(value: string) {
    this._title = value;
    this.elementRef.nativeElement.title = '';
  }

  @Input()
  border: PanelBorderType = 'Single';

  @Input()
  set flat(value: boolean) {
    this.border = value ? 'Flat' : 'Single';
    this.elevated = !value;
  }
  get flat(): boolean {
    return this.border === 'Flat';
  }

  @Input()
  expanded: boolean = true;

  @Input()
  elevated: boolean = false;

  @Input()
  actionTemplate: TemplateRef<any>;

  @Input()
  labelTemplate: TemplateRef<any>;

  @Input()
  sentenceCase = true;

  @Input()
  canExpand: boolean = true;

  @Input()
  overflowHeight: string = null;

  @Input()
  minHeight: string = null;

  @Input()
  panelId: string = '';

  @Input()
  sectionName: string = null;

  @Input()
  featureName: string = null;

  private _title: string;

  constructor(private elementRef: ElementRef, public logService: LogService) {
    super();
  }

  toggleExpand(canToggle: boolean = true) {
    if (this.expanded == false && this.sectionName != null) {
      this.FeatureLogging(this.featureName, this.title, this.sectionName);
    }
    if (canToggle && this.canExpand) {
      this.expanded = !this.expanded;
    }
  }
}

export type PanelBorderType =
  | 'Flat'
  | 'FlatPadded'
  | 'Single'
  | 'Empty'
  | 'None';

export type HeaderLevel = 1 | 2 | 3 | 4 | 5;
